import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { downloadJson, toFileNameSegment } from 'utils/downloadJson';
import ImportMenuLayout, { ACCENT } from './ImportMenuLayout';

/**
 * Reusable Import Menu view (UI + flow). Request behaviour is injected via
 * the `onImport` and `onEmpty` callbacks so the same UI can target different
 * backends (POS menu vs. regular menu).
 *
 * @param {string} businessUuid
 * @param {string} breakGlassPage              break-glass log "page" identifier
 * @param {string} breakGlassEventType         break-glass log event type
 * @param {string} emptyActionSummary          human readable action summary
 * @param {(parsedMenu: object) => Promise} onImport   throws on failure
 * @param {() => Promise} onEmpty              throws on failure
 * @param {() => Promise<object>} [onExport]   resolves with the menu JSON in
 *                                             the same shape `onImport` accepts;
 *                                             the export card is hidden when omitted
 * @param {string} [exportFileType]            file name segment, e.g. 'pos-menu'
 * @param {string} [businessName]              file name segment
 */
const ImportMenuView = ({
  businessUuid,
  breakGlassPage,
  breakGlassEventType = 'menu-import-action',
  emptyActionSummary = 'Empty menu for this business',
  onImport,
  onEmpty,
  onExport,
  exportFileType,
  businessName,
  title = 'Import Menu',
  subtitle = 'Import a menu from a JSON file, start from an empty menu, or connect a POS provider.',
  importSuccessMessage = 'Menu imported successfully.',
  emptySuccessMessage = 'Menu emptied successfully.',
  comingSoonProviders,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedMenu, setParsedMenu] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const activeStep = parsedMenu ? 1 : 0;

  const ensureBusiness = () => {
    if (!businessUuid) {
      toast.error(t('No business selected'));
      return false;
    }
    return true;
  };

  // Step 1: choose / upload a JSON file (parsed and held in state, not sent yet)
  const handleChooseFile = () => {
    if (!ensureBusiness()) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again re-triggers change
    event.target.value = '';
    if (!file) return;
    if (!ensureBusiness()) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      let parsed;
      try {
        parsed = JSON.parse(e.target.result);
      } catch {
        toast.error(t('Invalid JSON file'));
        setFileName('');
        setParsedMenu(null);
        return;
      }
      setFileName(file.name);
      setParsedMenu(parsed);
    };
    reader.onerror = () => {
      toast.error(t('Failed to read file'));
    };
    reader.readAsText(file);
  };

  const handleClearFile = () => {
    setFileName('');
    setParsedMenu(null);
  };

  // Step 2: import the previously uploaded file
  const handleImport = async () => {
    if (!ensureBusiness()) return;
    if (!parsedMenu) {
      toast.error(t('Please upload a JSON file first'));
      return;
    }
    try {
      setSubmitting(true);
      await onImport(parsedMenu);
      handleClearFile();
      setSuccessMessage(t(importSuccessMessage));
    } catch {
      // Error toast handled by the request interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    if (!ensureBusiness()) return;
    try {
      setExporting(true);
      const menu = await onExport();
      const fileName = [
        toFileNameSegment(businessName),
        exportFileType,
        moment().format('YYYY-MM-DD'),
      ].join('-');
      downloadJson(menu, `${fileName}.json`);
    } catch {
      toast.error(t('Failed to export menu'));
    } finally {
      setExporting(false);
    }
  };

  const renderExportCard = ({ disabled }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #E5E7EB',
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 138, 0, 0.12)',
              color: ACCENT,
            }}
          >
            <FileDownloadIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('Export JSON menu')}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          sx={{ color: '#6B7280', mb: 2.5, minHeight: 40 }}
        >
          {t(
            'Download the current menu as a .json file that can be imported back.'
          )}
        </Typography>

        <Button
          fullWidth
          variant="contained"
          disableElevation
          startIcon={
            exporting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <FileDownloadIcon />
            )
          }
          onClick={handleExport}
          disabled={exporting || disabled || !businessUuid}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: ACCENT,
            '&:hover': { backgroundColor: '#E67C00' },
          }}
        >
          {t('Export JSON')}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <ImportMenuLayout
      businessUuid={businessUuid}
      breakGlassPage={breakGlassPage}
      breakGlassEventType={breakGlassEventType}
      emptyActionSummary={emptyActionSummary}
      emptySuccessMessage={emptySuccessMessage}
      onEmpty={onEmpty}
      busy={submitting}
      successMessage={successMessage}
      onSuccess={setSuccessMessage}
      onSuccessClose={() => setSuccessMessage('')}
      title={title}
      subtitle={subtitle}
      comingSoonProviders={comingSoonProviders}
      secondaryCard={onExport ? renderExportCard : undefined}
    >
      {({ disabled }) => (
        <Card
          elevation={0}
          sx={{
            height: '100%',
            border: '1px solid #E5E7EB',
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 138, 0, 0.12)',
                  color: ACCENT,
                }}
              >
                <CloudUploadIcon />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('Import JSON menu')}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 2.5 }}>
              {t(
                'Upload a .json menu file to map and import it into this business.'
              )}
            </Typography>

            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                mb: 3,
                '& .MuiStepIcon-root.Mui-active': { color: ACCENT },
                '& .MuiStepIcon-root.Mui-completed': { color: ACCENT },
              }}
            >
              <Step>
                <StepLabel>{t('Upload file')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('Import')}</StepLabel>
              </Step>
            </Stepper>

            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {!parsedMenu ? (
              <Box
                onClick={
                  !disabled && businessUuid ? handleChooseFile : undefined
                }
                sx={{
                  border: '2px dashed #D1D5DB',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor:
                    disabled || !businessUuid ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                  opacity: !businessUuid ? 0.6 : 1,
                  '&:hover': {
                    borderColor:
                      disabled || !businessUuid ? '#D1D5DB' : ACCENT,
                    backgroundColor:
                      disabled || !businessUuid
                        ? 'transparent'
                        : 'rgba(255, 138, 0, 0.04)',
                  },
                }}
              >
                <UploadFileIcon sx={{ color: '#9CA3AF', fontSize: 32 }} />
                <Typography
                  variant="body2"
                  sx={{ color: '#374151', fontWeight: 500, mt: 1 }}
                >
                  {t('Click to choose a JSON file')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  {t('Accepted format: .json')}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: '#F9FAFB',
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(34, 197, 94, 0.12)',
                    color: '#16A34A',
                    flexShrink: 0,
                  }}
                >
                  <FileIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#374151',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {fileName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#16A34A',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 14 }} />
                    {t('Ready to import')}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleClearFile}
                  disabled={disabled}
                  aria-label={t('Remove file')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={handleChooseFile}
                disabled={disabled || !businessUuid}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {parsedMenu ? t('Replace') : t('Upload file')}
              </Button>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                startIcon={
                  submitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CheckIcon />
                  )
                }
                onClick={handleImport}
                disabled={disabled || !businessUuid || !parsedMenu}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: ACCENT,
                  '&:hover': { backgroundColor: '#E67C00' },
                }}
              >
                {t('Import')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </ImportMenuLayout>
  );
};

export default ImportMenuView;
